import axios, { AxiosResponse } from 'axios';
import * as utils from '@/apis/apiUtils';
import type {
  EmergencyResponseTeamReportCreateParams,
  EmergencyResponseTeamReportBaseArrivalTsUpdateParams,
  EmergencyResponseTeamReportPhotoParams,
  EmergencyResponseTeamReportIndexParams,
} from '@/models/apis/externalReports/emergencyResponseTeam/emergencyResponseTeamReportRequest';
import type {
  EmergencyResponseTeamReportResponse,
  EmergencyResponseTeamReportPhoto,
  EmergencyResponseTeamReportPhotoCountResponse,
  EmergencyResponseTeamReportPhotoOrderResponse,
} from '@/models/apis/externalReports/emergencyResponseTeam/emergencyResponseTeamReportResponse';
import type {
  EmergencyResponseTeamReport,
  EmergencyResponseTeamReportPhotoCount,
} from '@/models/externalReports/emergencyResponseTeam/spEmergencyResponseTeamReport';
import { EmptyResponse } from '@/models/index';
import type {
  EmergencyResponseTeamReportWithPhotos,
  EmergencyResponseTeamReportPhotoExt,
  EmergencyResponseTeamPhotoOrder,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';

export default {
  async index(params: EmergencyResponseTeamReportIndexParams): Promise<EmergencyResponseTeamReportWithPhotos[]> {
    const { data } = await axios.get<EmergencyResponseTeamReportResponse[]>(
      utils.getApiUrl('/external_reports/emergency_response_team/reports'),
      {
        params: params || {},
      },
    );
    return convertEmergencyResponseTeamReportsResponseToEmergencyResponseTeamReports(data);
  },
  async createEmergencyResponseTeamReport(
    params: EmergencyResponseTeamReportCreateParams,
  ): Promise<EmergencyResponseTeamReport> {
    const { data } = await axios.post<EmergencyResponseTeamReportResponse>(
      utils.getApiUrl('/external_reports/emergency_response_team/reports'),
      params,
    );
    return convertEmergencyResponseTeamReportResponseToEmergencyResponseTeamReport(data);
  },
  async updateBaseArrival(
    id: number,
    params: EmergencyResponseTeamReportBaseArrivalTsUpdateParams,
  ): Promise<EmergencyResponseTeamReport> {
    const { data } = await axios.post<EmergencyResponseTeamReportResponse>(
      utils.getApiUrl(`/external_reports/emergency_response_team/reports/${id}/base_arrival_time`),
      params,
    );
    return convertEmergencyResponseTeamReportResponseToEmergencyResponseTeamReport(data);
  },
  async getPhotoCount(id: number): Promise<EmergencyResponseTeamReportPhotoCount[]> {
    const { data } = await axios.get<EmergencyResponseTeamReportPhotoCountResponse>(
      utils.getApiUrl(`/external_reports/emergency_response_team/reports/${id}/photo_count`),
    );
    return convertPhotoCountResponseToPhotoCount(data);
  },
  getPhotoOrders(reportId: number): Promise<AxiosResponse<EmergencyResponseTeamReportPhotoOrderResponse[]>> {
    return axios.get(utils.getApiUrl(`/external_reports/emergency_response_team/reports/${reportId}/photos/order`));
  },
  async addPhoto(
    id: number,
    params: EmergencyResponseTeamReportPhotoParams,
  ): Promise<EmergencyResponseTeamReportPhotoExt> {
    const formData = utils.transformFormData(params);
    const { data } = await axios.post<EmergencyResponseTeamReportPhoto>(
      utils.getApiUrl(`/external_reports/emergency_response_team/reports/${id}/photos`),
      formData,
    );
    return convertReportPhotosResponseToReportPhotos(data);
  },
  updatePhotosOrder(id: number, newOrders: EmergencyResponseTeamPhotoOrder[]): Promise<AxiosResponse<EmptyResponse>> {
    return axios.post(
      utils.getApiUrl(`/external_reports/emergency_response_team/reports/${id}/photos/order`),
      { new_orders: newOrders },
    );
  },
  updatePhotoComment(
    id: number,
    photoId: number,
    comment: string | null,
  ): Promise<AxiosResponse<EmergencyResponseTeamReportPhoto>> {
    return axios.post(
      utils.getApiUrl(`/external_reports/emergency_response_team/reports/${id}/photos/${photoId}/comment`),
      { comment: comment },
    );
  },
  deletePhoto(id: number, photoId: number): Promise<AxiosResponse<EmptyResponse>> {
    return axios.delete(
      utils.getApiUrl(`/external_reports/emergency_response_team/reports/${id}/photos/${photoId}`),
    );
  },
  async get(id: number): Promise<EmergencyResponseTeamReportWithPhotos> {
    const { data } = await axios.get<EmergencyResponseTeamReportResponse>(
      utils.getApiUrl(`/external_reports/emergency_response_team/reports/${id}`),
    );
    return convertEmergencyResponseTeamReportsResponseToEmergencyResponseTeamReports([data])[0];
  },
};

function convertEmergencyResponseTeamReportResponseToEmergencyResponseTeamReport(
  report: EmergencyResponseTeamReportResponse,
): EmergencyResponseTeamReport {
  return {
    id: report.id,
    group1Id: report.group1_id,
    emergencyResponseTeamCarId: report.car_id,
    baseDepartureTs: report.base_departure_ts ? new Date(report.base_departure_ts) : null,
    operationStartTs: report.operation_start_ts ? new Date(report.operation_start_ts) : null,
    operationEndTs: report.operation_end_ts ? new Date(report.operation_end_ts) : null,
    baseArrivalTs: report.base_arrival_ts ? new Date(report.base_arrival_ts) : null,
    hanName: report.han_name,
  };
}

function convertPhotoCountResponseToPhotoCount(
  response: EmergencyResponseTeamReportPhotoCountResponse,
): EmergencyResponseTeamReportPhotoCount[] {
  return response.photo_counts.map((item) => ({
    photoType: item.photo_type,
    count: item.count,
  }));
}

export function convertEmergencyResponseTeamReportsResponseToEmergencyResponseTeamReports(
  response: EmergencyResponseTeamReportResponse[],
): EmergencyResponseTeamReportWithPhotos[] {
  return response.map((item) => ({
    id: item.id,
    contents: item.contents,
    group1Id: Number(item.group1_id),
    emergencyResponseTeamCarId: Number(item.car_id),
    baseDepartureTs: item.base_departure_ts ? new Date(item.base_departure_ts) : null,
    operationStartTs: item.operation_start_ts ? new Date(item.operation_start_ts) : null,
    operationEndTs: item.operation_end_ts ? new Date(item.operation_end_ts) : null,
    baseArrivalTs: item.base_arrival_ts ? new Date(item.base_arrival_ts) : null,
    companyId: item.company_id ?? null,
    companyName: item.company_name,
    hanName: item.han_name,
    otherContent: item.other_content,
    roadNames: item.road_names,
    photos: item.photos.map((photo) =>
      convertReportPhotosResponseToReportPhotos(photo),
    ),
  }));
}

export function convertReportPhotosResponseToReportPhotos(
  response: EmergencyResponseTeamReportPhoto,
): EmergencyResponseTeamReportPhotoExt {
  return {
    id: response.id,
    ts: response.ts,
    lat: Number(response.lat),
    lon: Number(response.lon),
    photoType: response.photo_type,
    imagePath: response.image_path,
    comment: response.comment,
    companyName: null,
    dispOrder: response.disp_order,
    isSelected: false,
    hanName: '',
    savedImage: null,
    samePosPhotos: [],
  };
}
